<script setup lang="ts">
import { formatUsd } from '~/constants/pricing'

/**
 * The partner's statistics: a 7/30/90-day range, headline numbers with
 * conversion rates, clicks and earnings per day, and clicks per source.
 */
interface StatsResponse {
  range: number
  days: { date: string, clicks: number, signups: number, orders: number, earningsCents: number }[]
  totals: {
    clicks: number
    signups: number
    orders: number
    earningsCents: number
    signupRate: number | null
    orderRate: number | null
    earningsPerClickCents: number | null
  }
  sources: { source: string, clicks: number }[]
}

const { t, te } = useI18n()
const range = ref<7 | 30 | 90>(30)

const { data, pending, error } = useFetch<StatsResponse>('/api/affiliate/stats', {
  query: { days: range },
  server: false,
})

const money = (cents: number) => formatUsd(cents / 100)
const percent = (value: number | null) => value === null ? '–' : `${(value * 100).toFixed(1)} %`

const tiles = computed(() => {
  const totals = data.value?.totals
  if (!totals) return []
  return [
    { label: t('affiliate.stats.clicks'), value: totals.clicks.toLocaleString() },
    { label: t('affiliate.stats.referrals'), value: totals.signups.toLocaleString(), hint: t('affiliate.statsPanel.rateOfClicks', { rate: percent(totals.signupRate) }) },
    { label: t('affiliate.stats.orders'), value: totals.orders.toLocaleString(), hint: t('affiliate.statsPanel.rateOfClicks', { rate: percent(totals.orderRate) }) },
    { label: t('affiliate.statsPanel.earnings'), value: money(totals.earningsCents), hint: totals.earningsPerClickCents === null ? undefined : t('affiliate.statsPanel.perClick', { amount: money(totals.earningsPerClickCents) }) },
  ]
})

const dates = computed(() => data.value?.days.map(day => day.date) ?? [])
const clicks = computed(() => data.value?.days.map(day => day.clicks) ?? [])
const earnings = computed(() => data.value?.days.map(day => day.earningsCents) ?? [])

/** Tags the dashboard itself puts on links get a name; the partner's own ones show as typed. */
function sourceLabel(source: string): string {
  if (!source) return t('affiliate.statsPanel.directLink')
  const share = /^share-(\w+)$/.exec(source)
  if (share && te(`affiliate.share.networks.${share[1]}`)) return t('affiliate.statsPanel.sourceNames.share', { network: t(`affiliate.share.networks.${share[1]}`) })
  if (share?.[1] === 'native') return t('affiliate.statsPanel.sourceNames.share', { network: t('affiliate.share.more') })
  const known: Record<string, string> = { 'page': 'page', 'qr': 'qr', 'widget': 'widget', 'widget-ats': 'widget', 'widget-letter': 'widget' }
  return known[source] ? `${t(`affiliate.statsPanel.sourceNames.${known[source]}`)}${source.startsWith('widget-') ? ` (${source.slice(7)})` : ''}` : source
}
</script>

<template>
  <section>
    <div class="flex flex-wrap items-center justify-between gap-4">
      <h2 class="!text-xl">{{ t('affiliate.statsPanel.title') }}</h2>
      <div class="inline-flex rounded-full border border-line p-1 dark:border-gray-700" role="group" :aria-label="t('affiliate.statsPanel.range')">
        <button
          v-for="days in [7, 30, 90] as const"
          :key="days"
          type="button"
          class="whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-semibold transition-colors duration-300"
          :class="range === days ? 'bg-ink text-white dark:bg-white dark:text-ink' : 'text-muted hover:text-ink dark:text-gray-400 dark:hover:text-white'"
          :aria-pressed="range === days"
          @click="range = days"
        >
          {{ t('affiliate.statsPanel.days', { days }) }}
        </button>
      </div>
    </div>

    <SofaxNotice v-if="error" type="error" class="mt-4">{{ t('affiliate.statsPanel.error') }}</SofaxNotice>

    <div v-else-if="pending && !data" class="flex items-center justify-center py-16 text-muted dark:text-gray-400">
      <i class="bi bi-arrow-repeat animate-spin text-2xl" />
    </div>

    <template v-else-if="data">
      <div class="mt-4 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <div v-for="tile in tiles" :key="tile.label" class="rounded-[0.625rem] border border-line p-5 dark:border-gray-700">
          <div class="text-sm text-muted dark:text-gray-400">{{ tile.label }}</div>
          <div class="mt-1 font-title text-2xl font-bold text-ink dark:text-white">{{ tile.value }}</div>
          <div v-if="tile.hint" class="mt-1 text-xs text-muted dark:text-gray-400">{{ tile.hint }}</div>
        </div>
      </div>

      <div class="mt-6 grid gap-6 lg:grid-cols-2">
        <div class="rounded-[0.625rem] border border-line p-5 dark:border-gray-700">
          <h3 class="font-title !text-base font-semibold text-ink dark:text-white">{{ t('affiliate.statsPanel.clicksPerDay') }}</h3>
          <AffiliateStatsChart class="mt-4" :dates="dates" :values="clicks" :label="t('affiliate.statsPanel.clicksPerDay')" />
        </div>
        <div class="rounded-[0.625rem] border border-line p-5 dark:border-gray-700">
          <h3 class="font-title !text-base font-semibold text-ink dark:text-white">{{ t('affiliate.statsPanel.earningsPerDay') }}</h3>
          <AffiliateStatsChart class="mt-4" :dates="dates" :values="earnings" :format="money" :label="t('affiliate.statsPanel.earningsPerDay')" />
        </div>
      </div>

      <div class="mt-6">
        <h3 class="font-title !text-base font-semibold text-ink dark:text-white">{{ t('affiliate.statsPanel.sources') }}</h3>
        <p v-if="!data.sources.length" class="mt-2 text-sm text-muted dark:text-gray-400">{{ t('affiliate.statsPanel.noClicks') }}</p>
        <div v-else class="mt-3 overflow-x-auto rounded-[0.625rem] border border-line dark:border-gray-700">
          <table class="w-full text-sm">
            <thead class="bg-surface font-title font-semibold text-ink dark:bg-gray-800 dark:text-white">
              <tr>
                <th class="px-4 py-3 text-left">{{ t('affiliate.statsPanel.source') }}</th>
                <th class="px-4 py-3 text-right">{{ t('affiliate.stats.clicks') }}</th>
                <th class="px-4 py-3 text-right">{{ t('affiliate.statsPanel.share') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in data.sources" :key="row.source" class="border-t border-line dark:border-gray-700">
                <td class="px-4 py-3 font-mono">{{ sourceLabel(row.source) }}</td>
                <td class="px-4 py-3 text-right">{{ row.clicks.toLocaleString() }}</td>
                <td class="px-4 py-3 text-right">{{ percent(data.totals.clicks ? row.clicks / data.totals.clicks : null) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p class="mt-2 text-xs text-muted dark:text-gray-400">{{ t('affiliate.statsPanel.sourcesHint') }}</p>
      </div>
    </template>
  </section>
</template>
